import React, { useState, useMemo } from 'react';
import { 
  Network, 
  Search, 
  ExternalLink,
  SlidersHorizontal
} from 'lucide-react';
import { 
  SYSTEM_CAPABILITIES, 
  CAPABILITY_DOMAINS, 
  CapabilityDomainId, 
  LifecycleStatus, 
  ReuseBadge
} from '../../shared/constants/systemCapabilities';

export const SystemCapabilityMapView: React.FC = () => {
  const [selectedDomain, setSelectedDomain] = useState<CapabilityDomainId | 'all'>('all');
  const [selectedLifecycle, setSelectedLifecycle] = useState<LifecycleStatus | 'all'>('all');
  const [selectedReuse, setSelectedReuse] = useState<ReuseBadge | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Stats
  const totalCount = SYSTEM_CAPABILITIES.length;
  const stableCount = SYSTEM_CAPABILITIES.filter(c => c.lifecycle === 'STABLE').length;
  const sharedCount = SYSTEM_CAPABILITIES.filter(c => c.reuse === 'SHARED').length;
  const dedicatedCount = SYSTEM_CAPABILITIES.filter(c => c.reuse === 'DEDICATED').length;

  // Filtered Capabilities
  const filteredCapabilities = useMemo(() => {
    return SYSTEM_CAPABILITIES.filter(cap => {
      if (selectedDomain !== 'all' && cap.domainId !== selectedDomain) return false;
      if (selectedLifecycle !== 'all' && cap.lifecycle !== selectedLifecycle) return false;
      if (selectedReuse !== 'all' && cap.reuse !== selectedReuse) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchName = cap.name.toLowerCase().includes(q);
        const matchOutcome = cap.valueOutcome.toLowerCase().includes(q);
        const matchPointer = cap.pointerText.toLowerCase().includes(q);
        const matchDomain = cap.domainNameZh.toLowerCase().includes(q);
        if (!matchName && !matchOutcome && !matchPointer && !matchDomain) return false;
      }
      return true;
    });
  }, [selectedDomain, selectedLifecycle, selectedReuse, searchQuery]);

  return (
    <div className="capability-map-container" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* 1. Header Banner */}
      <div 
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: 'var(--shadow-card)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div 
                style={{ 
                  width: '26px', 
                  height: '26px', 
                  borderRadius: '6px', 
                  background: 'var(--bg-surface-hover)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'var(--color-bullish)'
                }}
              >
                <Network size={16} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  全域功能架构与业务能力树
                </h2>
                <div style={{ fontSize: '9px', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.06em', marginTop: '1px' }}>
                  SYSTEM CAPABILITY MAP & FUNCTION TREE
                </div>
              </div>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
              依据 ASOP 规范建立的业务能力全景沙盘。涵盖四大核心业务域，提供端到端价值产出透析、生命周期状态索引与架构复用度视界。
            </p>
          </div>

          {/* Quick Metrics Bar */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ padding: '4px 10px', background: 'var(--bg-surface-elevated)', borderRadius: '6px', border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>全域原子能力</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginTop: '1px' }}>{totalCount}</div>
            </div>
            <div style={{ padding: '4px 10px', background: 'var(--bg-surface-elevated)', borderRadius: '6px', border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>生产稳定 (STABLE)</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--color-bullish)', marginTop: '1px' }}>{stableCount}</div>
            </div>
            <div style={{ padding: '4px 10px', background: 'var(--bg-surface-elevated)', borderRadius: '6px', border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>全局公共底座 (SHARED)</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--color-cyan)', marginTop: '1px' }}>{sharedCount}</div>
            </div>
            <div style={{ padding: '4px 10px', background: 'var(--bg-surface-elevated)', borderRadius: '6px', border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>业务专有特性 (DEDICATED)</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginTop: '1px' }}>{dedicatedCount}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Filter & Search Controls */}
      <div 
        style={{ 
          background: 'var(--bg-card)', 
          border: '1px solid var(--border-subtle)', 
          borderRadius: '8px', 
          padding: '14px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          {/* Domain Tabs */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <SlidersHorizontal size={14} /> 业务域:
            </span>
            <button
              onClick={() => setSelectedDomain('all')}
              aria-pressed={selectedDomain === 'all'}
              className={`capability-filter-btn ${selectedDomain === 'all' ? 'active' : ''}`}
            >
              全部 ({totalCount})
            </button>
            {CAPABILITY_DOMAINS.map(d => {
              const count = SYSTEM_CAPABILITIES.filter(c => c.domainId === d.id).length;
              const isSelected = selectedDomain === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedDomain(d.id)}
                  aria-pressed={isSelected}
                  className={`capability-filter-btn ${isSelected ? 'active' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <span>{d.icon}</span>
                  <span>{d.nameZh}</span>
                  <span style={{ opacity: 0.7, fontSize: '11px' }}>({count})</span>
                </button>
              );
            })}
          </div>

          {/* Keyword Search */}
          <div style={{ position: 'relative', minWidth: '240px' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              aria-label="搜索能力、价值产出或链路"
              placeholder="搜索能力、价值产出或链路..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 12px 6px 30px',
                fontSize: '12px',
                background: 'var(--bg-surface-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '6px',
                color: 'var(--text-primary)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Sub-filters for Reuse & Lifecycle */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', paddingTop: '8px', borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>复用属性:</span>
            {(['all', 'SHARED', 'DEDICATED', 'OVERLAP_CANDIDATE'] as const).map(badge => (
              <button
                key={badge}
                onClick={() => setSelectedReuse(badge)}
                aria-pressed={selectedReuse === badge}
                className={`capability-subfilter-btn ${selectedReuse === badge ? 'active' : ''}`}
              >
                {badge === 'all' ? '全部' : badge}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>生命周期:</span>
            {(['all', 'STABLE', 'EVOLVING', 'PLANNED', 'DEPRECATED'] as const).map(status => (
              <button
                key={status}
                onClick={() => setSelectedLifecycle(status)}
                aria-pressed={selectedLifecycle === status}
                className={`capability-subfilter-btn ${selectedLifecycle === status ? 'active' : ''}`}
              >
                {status === 'all' ? '全部' : status}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 3. Capability Domain Groups */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {filteredCapabilities.length === 0 ? (
          <div
            style={{
              padding: '32px 20px',
              textAlign: 'center',
              background: 'var(--bg-card)',
              border: '1px dashed var(--border-subtle)',
              borderRadius: '8px',
              color: 'var(--text-muted)',
              fontSize: '13px'
            }}
          >
            未找到匹配的系统能力项，请调整搜索关键字或筛选条件。
          </div>
        ) : CAPABILITY_DOMAINS.filter(d => selectedDomain === 'all' || selectedDomain === d.id).map(domain => {
          const itemsInDomain = filteredCapabilities.filter(c => c.domainId === domain.id);
          if (itemsInDomain.length === 0) return null;

          return (
            <div 
              key={domain.id}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-card)'
              }}
            >
              {/* Domain Header */}
              <div 
                style={{
                  padding: '14px 20px',
                  background: 'var(--bg-surface-elevated)',
                  borderBottom: '1px solid var(--border-subtle)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>{domain.icon}</span>
                  <div>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {domain.nameZh}
                    </span>
                    <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--text-muted)', marginLeft: '8px', letterSpacing: '0.06em' }}>
                      {domain.nameEn}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {domain.description}
                </div>
              </div>

              {/* Domain Capability Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)', fontSize: '11px' }}>
                      <th style={{ padding: '10px 18px', width: '22%' }}>业务能力名称</th>
                      <th style={{ padding: '10px 18px', width: '40%' }}>核心业务价值产出</th>
                      <th style={{ padding: '10px 14px', width: '12%', textAlign: 'center' }}>生命周期</th>
                      <th style={{ padding: '10px 14px', width: '12%', textAlign: 'center' }}>复用度标识</th>
                      <th style={{ padding: '10px 18px', width: '14%' }}>权威锚点指针</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsInDomain.map(cap => {
                      return (
                        <tr 
                          key={cap.id}
                          className="capability-table-row"
                        >
                          <td style={{ padding: '12px 18px', fontWeight: 600, color: 'var(--text-primary)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>{cap.name}</span>
                              {cap.route && (
                                <a 
                                  href={cap.route}
                                  title="点击直达该视图"
                                  style={{ color: 'var(--color-cyan)', display: 'inline-flex', alignItems: 'center' }}
                                >
                                  <ExternalLink size={12} />
                                </a>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px 18px', color: 'var(--text-primary)', lineHeight: '1.4' }}>
                            {cap.valueOutcome}
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                            {cap.lifecycle === 'STABLE' && (
                              <span className="capability-map-badge capability-badge-stable">
                                🟢 STABLE
                              </span>
                            )}
                            {cap.lifecycle === 'EVOLVING' && (
                              <span className="capability-map-badge capability-badge-evolving">
                                🟡 EVOLVING
                              </span>
                            )}
                            {cap.lifecycle === 'PLANNED' && (
                              <span className="capability-map-badge capability-badge-planned">
                                ⚪ PLANNED
                              </span>
                            )}
                            {cap.lifecycle === 'DEPRECATED' && (
                              <span className="capability-map-badge capability-badge-deprecated">
                                🔴 DEPRECATED
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                            {cap.reuse === 'SHARED' && (
                              <span className="capability-map-badge capability-badge-shared">
                                SHARED
                              </span>
                            )}
                            {cap.reuse === 'DEDICATED' && (
                              <span className="capability-map-badge capability-badge-dedicated">
                                DEDICATED
                              </span>
                            )}
                            {cap.reuse === 'OVERLAP_CANDIDATE' && (
                              <span className="capability-map-badge capability-badge-overlap">
                                ⚠️ OVERLAP
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px 18px', color: 'var(--text-muted)', fontSize: '12px' }}>
                            <code style={{ fontSize: '11px', background: 'var(--code-bg)', padding: '2px 6px', borderRadius: '4px' }}>
                              {cap.pointerText}
                            </code>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
